import { Button, Col, Row } from 'antd';
import moment from 'moment';
import { Colors } from 'works/legacy/lib/style-helper';
import React from 'react';
import { ShiftGapDetailsQueryCgResponse } from '@common/graphql/schema';
import { Gap, Icon, Text } from '@common/components';
import { getShiftSourceDisplay } from '@scheduling/services';
import { formatNumberDigits } from '@common/services';

export function ShiftRequestDetailsContent({
  shift,
  setShiftEditMode = () => {},
  showOpeningCount,
  matchBreakdownEditMode = false,
  canEditShiftDetails = false,
  tagsEnabled = false,
}: {
  shift: ShiftGapDetailsQueryCgResponse['shiftGap'];
  showOpeningCount?: boolean;
  setShiftEditMode?: (value: boolean) => void;
  matchBreakdownEditMode?: boolean;
  canEditShiftDetails?: boolean;
  tagsEnabled?: boolean;
}) {
  const isNightShift = shift.timeOfDay === 'night';

  const shiftStart = moment.utc(shift.startDatetime).tz(shift.timeZone);
  const shiftEnd = moment.utc(shift.endDatetime).tz(shift.timeZone);

  const shiftRangeText = `${shiftStart.format('HH:mm')} - ${shiftEnd.format(
    'HH:mm'
  )}`;

  const titleSpan = showOpeningCount ? 4 : 3;
  const shiftSourceDisplay = getShiftSourceDisplay(shift?.source);

  const sourceInfo = (
    <Gap align="center" size="xs">
      <Icon icon={shiftSourceDisplay.icon} kind="light" />
      <Text>{shiftSourceDisplay.label}</Text>
    </Gap>
  );

  return (
    <>
      <Row justify="space-between">
        <Row gutter={[8, 20]} align="middle">
          <Col>
            <Text size="lg" weight="medium">
              {shiftStart.format('dddd, MMMM D')}{' '}
            </Text>
          </Col>
          <Col>
            <Icon
              icon={isNightShift ? 'moon' : 'sun'}
              kind="solid"
              size="md"
              color={isNightShift ? 'skyRegular' : 'harvestGold'}
            />
          </Col>
          <Col>
            <Text size="lg" weight="medium">
              {shiftRangeText} ({formatNumberDigits(shift.shiftDuration)} hrs)
            </Text>
          </Col>
          {(canEditShiftDetails || tagsEnabled) && !matchBreakdownEditMode && (
            <Button type="link" onClick={() => setShiftEditMode(true)}>
              <Icon icon="pen" kind="solid" size="md" />
            </Button>
          )}
        </Row>
        {sourceInfo}
      </Row>
      <div style={{ fontSize: '14px', marginTop: '16px' }}>
        <Row gutter={[0, 8]}>
          <Col span={titleSpan}>
            <Text secondary>Role</Text>
          </Col>
          <Col span={16}>{shift?.role?.abbreviation}</Col>
        </Row>

        <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
          <Col span={titleSpan}>
            <Text secondary>Unit</Text>
          </Col>
          <Col span={16}>{shift.organizationUnit.name}</Col>
        </Row>

        <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
          <Col span={titleSpan}>
            <Text secondary>CC</Text>
          </Col>
          <Col span={16}>{shift.organizationUnit.costCenter}</Col>
        </Row>

        {showOpeningCount && (
          <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
            <Col span={titleSpan}>
              <Text secondary>Openings</Text>
            </Col>
            <Col span={24 - titleSpan}>{shift.openShiftGapOpeningsCount}</Col>
          </Row>
        )}

        {tagsEnabled && (
          <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
            <Col span={titleSpan}>
              <Text secondary>Tags</Text>
            </Col>
            {shift.shiftGapTags &&
              shift.shiftGapTags.map((tag) => (
                <div
                  key={tag.id}
                  style={{
                    backgroundColor: Colors.skyLightest,
                    cursor: 'pointer',
                    marginRight: 8,
                    padding: '0px 12px',
                    borderRadius: 24,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Text weight="medium">{tag.name}</Text>
                </div>
              ))}
            {!shift.shiftGapTags?.length && <Col span={16}>None</Col>}
          </Row>
        )}
      </div>
    </>
  );
}
